import React, { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../AuthProvider";
import { getAuth } from "firebase/auth";
import { NODE_ENV } from "../../constants/config";
import {
  FaRobot,
  FaTimesCircle,
  FaPaperPlane,
  FaSpinner,
  FaPlus,
  FaEye,
  FaCopy,
  FaSave,
  FaFileExport,
  FaExclamationTriangle,
  FaPhone,
  FaCheck,
} from "react-icons/fa";
import { toast } from "react-toastify";
import VoiceMailService from "../services/VoiceMailService";

const EmailGeneratorModal = ({ contact, teamId, onClose, onSave }) => {
  const { user } = useContext(AuthContext);
  const [customContext, setCustomContext] = useState("");
  const [generateCount, setGenerateCount] = useState(1);
  const [loading, setLoading] = useState(false);
  const [emailSamples, setEmailSamples] = useState([]);
  const [selectedSample, setSelectedSample] = useState(null);
  const [selectedEmailKey, setSelectedEmailKey] = useState(
    "day1-problem-solution"
  );
  const [selectedPrompts, setSelectedPrompts] = useState([]);
  const [showRegenerateWarning, setShowRegenerateWarning] = useState(false);
  const [voiceMail, setVoiceMail] = useState(null);
  const [voiceMailLoading, setVoiceMailLoading] = useState(false);
  const [voiceMailError, setVoiceMailError] = useState("");
  const [voiceMailCopied, setVoiceMailCopied] = useState(false);

  // Check if contact already has samples (for limit warning)
  const existingSamplesCount = contact.generatedEmailSamples?.length || 0;
  const maxSamples = 3;

  const emailLabels = {
    "day1-problem-solution": "Day 1 - Problem Solution",
    "day4-case-study": "Day 4 - Case Study",
    "day7-role-value": "Day 7 - Role Value",
    "day10-urgency": "Day 10 - Urgency",
    "day14-breakup": "Day 14 - Breakup",
  };

  useEffect(() => {
    setVoiceMail(null);
    setVoiceMailError("");
    setVoiceMailCopied(false);
  }, [selectedSample, selectedEmailKey]);

  // Standard prompt templates
  const promptTemplates = [
    {
      id: "subject_name_meeting",
      text: 'Subject must begin with the prospect\'s first name and include the phrase "meeting next week"; keep 5–8 words; sentence case; no emojis.',
      category: "Subject Line",
    },
    {
      id: "personalize_first_sentence",
      text: "Personalize the first sentence with one concrete fact from LinkedIn/company data (role change, sustainability target, site name).",
      category: "Personalization",
    },
    {
      id: "word_limit_cta",
      text: "Keep email ≤110 words, 3–5 short sentences, one CTA.",
      category: "Structure",
    },
    {
      id: "meeting_options",
      text: "Offer two 15–20 min options next week in the prospect's timezone and include {{Calendar_Link}} as fallback.",
      category: "CTA",
    },
    {
      id: "scope3_outcomes",
      text: "Tie outcomes to Scope-3, supplier scorecards, third-party verification, and audit-ready chain-of-custody.",
      category: "Value Proposition",
    },
    {
      id: "avoid_pricing",
      text: "Avoid landfill-fee/price/ROI language and generic buzzwords.",
      category: "Messaging",
    },
    {
      id: "peer_proof",
      text: "If a credible peer exists, add one sentence of peer proof (verified zero-landfill + monthly evidence).",
      category: "Social Proof",
    },
    {
      id: "diagnostic_link",
      text: "Offer a private diagnostic link pre-loaded with {{Company}} assumptions (one sentence).",
      category: "CTA",
    },
    {
      id: "preview_text",
      text: 'Generate preview text (60–80 chars) mentioning "meeting next week" + one sustainability gain.',
      category: "Email Settings",
    },
    {
      id: "site_specific_cta",
      text: "If {{SiteName}} is known, center the CTA on piloting at that site with week-1 milestones.",
      category: "CTA",
    },
    {
      id: "compliance_context",
      text: "Maintain U.S. compliance context and traceable movements; avoid claims you cannot support.",
      category: "Compliance",
    },
  ];

  const togglePrompt = (promptId) => {
    setSelectedPrompts((prev) =>
      prev.includes(promptId)
        ? prev.filter((id) => id !== promptId)
        : [...prev, promptId]
    );
  };

  const generateVoiceMailFromSelectedEmail = async () => {
    const emailContent =
      selectedSample?.emails?.[selectedEmailKey]?.trim() || "";

    if (!emailContent) {
      setVoiceMailError(
        "Select an email with content before generating a voice mail."
      );
      return;
    }

    setVoiceMailLoading(true);
    setVoiceMailError("");
    setVoiceMailCopied(false);

    try {
      const result = await VoiceMailService.generateVoiceMail({
        emailSample: emailContent,
        contactName:
          contact.fullName || contact.name || contact.firstName || "Prospect",
        contactTitle: contact.title || contact.role || "",
        companyName: contact.companyName || contact.company || "",
        contactEmail: contact.email || "",
        sellerName: user?.displayName || "ReWorld SDR Team",
        sellerRole: "Sales Development Representative",
        sellerCompany: "ReWorld",
        campaignGoal: "Book a meeting",
      });

      setVoiceMail(result);
      toast.success("Voice mail generated.");
    } catch (error) {
      console.error("Voice mail generation failed:", error);
      const message =
        error.message ||
        "Failed to generate the voice mail. Please try again.";
      setVoiceMailError(message);
      toast.error(message);
    } finally {
      setVoiceMailLoading(false);
    }
  };

  const copyVoiceMailToClipboard = () => {
    if (!voiceMail?.script) {
      return;
    }
    navigator.clipboard.writeText(voiceMail.script);
    setVoiceMailCopied(true);
    toast.success("Voice mail copied!");
    setTimeout(() => setVoiceMailCopied(false), 2000);
  };

  const generateEmails = async (forceRegenerateInput = false) => {
    const forceRegenerate =
      typeof forceRegenerateInput === "boolean" ? forceRegenerateInput : false;
    if (!user) {
      toast.error("You must be logged in to generate emails");
      return;
    }

    // Check sample limit
    if (existingSamplesCount >= maxSamples && !forceRegenerate) {
      setShowRegenerateWarning(true);
      return;
    }

    setLoading(true);

    try {
      // Get Firebase auth token (same pattern as other components)
      const idToken = user
        ? await getAuth().currentUser.getIdToken(true)
        : null;
      if (!idToken) {
        throw new Error("Authentication required");
      }

      // Prepare contact and company data
      const contactData = `
Name: ${contact.fullName || contact.name}
Title: ${contact.title}
Location: ${contact.location}

${contact.personSummary || contact.summary || "No profile summary available"}

Work Experience:
${contact.workExperience || contact.work_ex || "No work experience data"}

Education:
${contact.education || "No education data"}
      `.trim();

      const companyData = `
Company Name: ${contact.companyName}
Industry: ${contact.industry}
Domain: ${contact.domain}

${
  contact.companySummary ||
  contact.company_summary ||
  "No company summary available"
}
      `.trim();

      console.log("Generating emails with data:", {
        contactEmail: contact.email,
        hasContactData: !!contactData,
        hasCompanyData: !!companyData,
        customContext: customContext.substring(0, 100) + "...",
        selectedPrompts: selectedPrompts.length,
      });

      // Build combined context with selected prompts
      const selectedPromptTexts = selectedPrompts
        .map((id) => promptTemplates.find((p) => p.id === id)?.text)
        .filter(Boolean);

      const combinedContext = [
        customContext,
        ...selectedPromptTexts.map((text) => `• ${text}`),
      ]
        .filter(Boolean)
        .join("\n\n");

      // Prepare API base URL (consistent with other AI-SDR components)
      const apiBase =
        window.location.hostname.includes("localhost") ||
        window.location.hostname.includes("127.0.0.1")
          ? "http://127.0.0.1:5001/damia-gcp/us-central1/api"
          : "https://us-central1-damia-gcp.cloudfunctions.net/api";

      // Call the API
      const response = await fetch(`${apiBase}/generateReworldEmails`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          teamId: teamId || "zTS5IWYUGpICLRKioMPm",
          contactEmail: contact.email,
          contactData,
          companyData,
          customContext: combinedContext,
          generateCount: parseInt(generateCount),
          saveToContact: true,
          clearExisting: forceRegenerate, // Clear existing samples if regenerating
        }),
      });

      const result = await response.json();

      if (result.success) {
        setEmailSamples(result.emailSamples);
        setSelectedSample(result.emailSamples[0]);

        // Check if any samples had errors
        const successfulSamples = result.emailSamples.filter(
          (sample) => !sample.error
        );
        const errorSamples = result.emailSamples.filter(
          (sample) => sample.error
        );

        if (successfulSamples.length > 0) {
          toast.success(
            `Generated ${successfulSamples.length} email sample(s) successfully!`
          );
        }

        if (errorSamples.length > 0) {
          toast.warn(
            `${errorSamples.length} sample(s) failed to generate. Check the samples for details.`
          );
        }
      } else {
        throw new Error(result.error || "Email generation failed");
      }
    } catch (error) {
      console.error("Email generation error:", error);
      toast.error(`Failed to generate emails: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const copyEmailToClipboard = (emailContent) => {
    navigator.clipboard.writeText(emailContent);
    toast.success("Email copied to clipboard!");
  };

  const saveEmailsToContact = () => {
    if (onSave) {
      onSave(emailSamples);
    }
    toast.success("Email samples saved to contact!");
    onClose();
  };

  const downloadAllEmails = () => {
    if (!emailSamples.length) {
      toast.error("No email samples to download");
      return;
    }

    let content = `Email Sequences for ${contact.fullName || contact.name}\n`;
    content += `Email: ${contact.email}\n`;
    content += `Company: ${contact.companyName || "N/A"}\n`;
    content += `Generated: ${new Date().toLocaleString()}\n`;
    content += `\n${"=".repeat(60)}\n\n`;

    emailSamples.forEach((sample, sampleIndex) => {
      content += `${sample.sampleName || `Sample ${sampleIndex + 1}`}\n`;
      content += `Generated: ${new Date(
        sample.generatedAt
      ).toLocaleString()}\n`;
      if (sample.generatedBy) content += `By: ${sample.generatedBy}\n`;
      content += `\n${"-".repeat(40)}\n\n`;

      if (sample.error) {
        content += `ERROR: ${sample.error}\n\n`;
      } else {
        Object.entries(emailLabels).forEach(([key, label]) => {
          const email = sample.emails[key];
          if (email) {
            content += `${label}\n`;
            // Remove markdown formatting
            const cleanEmail = email
              .replace(/\*\*([^*]+)\*\*/g, "$1") // Remove bold
              .replace(/\*([^*]+)\*/g, "$1") // Remove italic
              .replace(/^#+ /gm, "") // Remove headers
              .replace(/^- /gm, "• ") // Convert dashes to bullets
              .trim();
            content += `${cleanEmail}\n\n`;
          }
        });
      }
      content += `\n${"=".repeat(60)}\n\n`;
    });

    // Create and download file
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${contact.fullName || contact.name}_email_sequences_${
      new Date().toISOString().split("T")[0]
    }.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success("Email sequences downloaded!");
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold flex items-center">
              <FaRobot className="mr-2 text-purple-600" />
              Generate Reworld Emails
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Generate personalized email sequences for{" "}
              {contact.fullName || contact.name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <FaTimesCircle size={24} />
          </button>
        </div>

        <div className="p-6">
          {/* Regeneration Warning */}
          {showRegenerateWarning && (
            <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <FaExclamationTriangle className="h-5 w-5 text-yellow-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Maximum Samples Reached
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>
                      This contact already has {existingSamplesCount} email
                      sample(s). The maximum is {maxSamples} samples per
                      contact.
                    </p>
                    <p className="mt-2">
                      To generate new samples, all existing samples will be
                      deleted and replaced.
                    </p>
                  </div>
                  <div className="mt-4 flex space-x-3">
                    <button
                      onClick={() => {
                        setShowRegenerateWarning(false);
                        generateEmails(true); // Force regenerate
                      }}
                      className="bg-yellow-600 text-white px-4 py-2 rounded text-sm hover:bg-yellow-700"
                    >
                      Delete Existing & Generate New
                    </button>
                    <button
                      onClick={() => setShowRegenerateWarning(false)}
                      className="bg-gray-300 text-gray-700 px-4 py-2 rounded text-sm hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Generation Form */}
          {emailSamples.length === 0 && !showRegenerateWarning && (
            <div className="space-y-6 mb-6">
              {/* Standard Prompt Templates */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Email Guidelines (Select applicable requirements)
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-60 overflow-y-auto border rounded-lg p-4">
                  {promptTemplates.map((prompt) => (
                    <div key={prompt.id} className="flex items-start space-x-2">
                      <input
                        type="checkbox"
                        id={prompt.id}
                        checked={selectedPrompts.includes(prompt.id)}
                        onChange={() => togglePrompt(prompt.id)}
                        className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor={prompt.id}
                        className="text-xs text-gray-700 cursor-pointer"
                      >
                        <span className="font-medium text-purple-600">
                          {prompt.category}:
                        </span>{" "}
                        {prompt.text}
                      </label>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Selected guidelines will be added to the generation prompt
                </p>
              </div>

              {/* Custom Context */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Context (Optional)
                </label>
                <textarea
                  value={customContext}
                  onChange={(e) => setCustomContext(e.target.value)}
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  rows={3}
                  placeholder="Add any specific context about this prospect, their challenges, or campaign focus..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  This will be combined with the contact's LinkedIn profile and
                  company data
                </p>
              </div>

              {/* Generation Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Email Samples
                  </label>
                  <select
                    value={generateCount}
                    onChange={(e) => setGenerateCount(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  >
                    <option value={1}>1 Sample</option>
                    <option value={2}>2 Samples</option>
                    <option value={3}>3 Samples</option>
                  </select>
                </div>

                <div className="flex items-end">
                  <div className="text-sm text-gray-600">
                    <div className="font-medium">
                      Selected Guidelines: {selectedPrompts.length}
                    </div>
                    <div className="text-xs">Will be applied to generation</div>
                  </div>
                </div>
              </div>

              {/* Sample limit warning */}
              {existingSamplesCount > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="text-sm text-blue-800">
                    <strong>Current samples:</strong> {existingSamplesCount}/
                    {maxSamples}
                    {existingSamplesCount >= maxSamples && (
                      <span className="text-yellow-600 ml-2">
                        (Maximum reached - will prompt to replace)
                      </span>
                    )}
                  </div>
                </div>
              )}

              <button
                onClick={generateEmails}
                disabled={loading}
                className="w-full bg-purple-600 text-white px-4 py-3 rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <FaSpinner className="animate-spin mr-2" />
                    Generating Emails...
                  </>
                ) : (
                  <>
                    <FaRobot className="mr-2" />
                    {existingSamplesCount >= maxSamples
                      ? "Replace All Email Samples"
                      : "Generate Email Sequences"}
                  </>
                )}
              </button>
            </div>
          )}

          {/* Generated Email Samples */}
          {emailSamples.length > 0 && (
            <div className="space-y-6">
              {/* Sample Selector */}
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">
                  Generated Email Samples
                </h3>
                <div className="flex space-x-2">
                  {emailSamples.map((sample) => (
                    <button
                      key={sample.sampleId}
                      onClick={() => setSelectedSample(sample)}
                      className={`px-3 py-1 rounded text-sm ${
                        selectedSample?.sampleId === sample.sampleId
                          ? "bg-purple-600 text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {sample.sampleName}
                    </button>
                  ))}
                </div>
              </div>

              {selectedSample && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Email List */}
                  <div className="lg:col-span-1">
                    <h4 className="font-medium mb-3">Email Sequence</h4>
                    <div className="space-y-2">
                      {Object.entries(emailLabels).map(([key, label]) => (
                        <button
                          key={key}
                          onClick={() => setSelectedEmailKey(key)}
                          className={`w-full text-left p-3 rounded border ${
                            selectedEmailKey === key
                              ? "border-purple-500 bg-purple-50"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <div className="font-medium text-sm">{label}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            {selectedSample.emails[key]
                              ? `${selectedSample.emails[key].substring(
                                  0,
                                  50
                                )}...`
                              : "No content"}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Email Content */}
                  <div className="lg:col-span-2">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium">
                        {emailLabels[selectedEmailKey]}
                      </h4>
                      <div className="flex space-x-2">
                        <button
                          onClick={() =>
                            copyEmailToClipboard(
                              selectedSample.emails[selectedEmailKey]
                            )
                          }
                          className="text-sm bg-gray-100 text-gray-700 px-3 py-1 rounded hover:bg-gray-200 flex items-center"
                        >
                          <FaCopy className="mr-1" />
                          Copy
                        </button>
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg border min-h-[300px]">
                      {selectedSample.error ? (
                        <div className="bg-red-50 border border-red-200 rounded p-4">
                          <div className="text-red-800 font-medium mb-2">
                            Generation Error
                          </div>
                          <div className="text-red-700 text-sm">
                            {selectedSample.error}
                          </div>
                          <div className="text-red-600 text-xs mt-2">
                            Try generating again or contact support if the issue
                            persists.
                          </div>
                        </div>
                      ) : (
                        <pre className="whitespace-pre-wrap text-sm text-gray-800">
                          {selectedSample.emails[selectedEmailKey] ||
                            "No content generated"}
                        </pre>
                      )}
                    </div>

                    <div className="mt-4 border border-purple-200 bg-purple-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="text-sm font-semibold text-purple-800 flex items-center">
                          <FaPhone className="mr-2" /> Voice Mail Touch
                        </h5>
                        <div className="flex space-x-2">
                          {voiceMail?.script && (
                            <button
                              onClick={copyVoiceMailToClipboard}
                              className="text-purple-700 hover:text-purple-900"
                              title="Copy voice mail"
                            >
                              {voiceMailCopied ? (
                                <FaCheck className="text-green-500" />
                              ) : (
                                <FaCopy />
                              )}
                            </button>
                          )}
                          <button
                            onClick={generateVoiceMailFromSelectedEmail}
                            disabled={voiceMailLoading}
                            className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center ${
                              voiceMailLoading
                                ? "bg-purple-200 text-purple-500 cursor-not-allowed"
                                : "bg-purple-600 text-white hover:bg-purple-700"
                            }`}
                          >
                            {voiceMailLoading ? (
                              <>
                                <FaSpinner className="animate-spin mr-2" />
                                Crafting...
                              </>
                            ) : (
                              <>
                                <FaPhone className="mr-2" />
                                Generate Voice Mail
                              </>
                            )}
                          </button>
                        </div>
                      </div>

                      {voiceMailError && (
                        <div className="mb-2 bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded">
                          {voiceMailError}
                        </div>
                      )}

                      {voiceMailLoading ? (
                        <div className="flex items-center text-xs text-purple-700">
                          <FaSpinner className="animate-spin mr-2" />
                          Drafting a voicemail that nudges them back to this
                          email...
                        </div>
                      ) : voiceMail?.script ? (
                        <div className="space-y-3 text-xs text-gray-800">
                          <div className="bg-white border border-purple-100 rounded-md p-3 whitespace-pre-line">
                            {voiceMail.script}
                          </div>
                          {voiceMail.callToAction && (
                            <div className="text-purple-800 font-semibold">
                              CTA: {voiceMail.callToAction}
                            </div>
                          )}
                          {Array.isArray(voiceMail.keyPoints) &&
                            voiceMail.keyPoints.length > 0 && (
                              <div>
                                <p className="text-[11px] font-semibold text-purple-700 mb-1">
                                  Key Reminders
                                </p>
                                <ul className="list-disc list-inside text-[11px] text-purple-700 space-y-1">
                                  {voiceMail.keyPoints.map((point, idx) => (
                                    <li key={idx}>{point}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                        </div>
                      ) : (
                        <p className="text-xs text-purple-700">
                          Generate a quick ReWorld voicemail that points the
                          prospect back to this email and a 15-minute meeting.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-between pt-4 border-t">
                <button
                  onClick={() => {
                    setEmailSamples([]);
                    setSelectedSample(null);
                  }}
                  className="text-gray-600 hover:text-gray-800"
                >
                  Generate New Samples
                </button>

                <div className="flex space-x-3">
                  <button
                    onClick={downloadAllEmails}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
                  >
                    <FaFileExport className="mr-2" />
                    Download All
                  </button>
                  <button
                    onClick={onClose}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Close
                  </button>
                  <button
                    onClick={saveEmailsToContact}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center"
                  >
                    <FaSave className="mr-2" />
                    Save to Contact
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailGeneratorModal;
